import { money, whole, type Axis, type PricePoint } from "../lib/flightPrices";

const moment = (iso: string) =>
  new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZone: "America/Los_Angeles",
  }).format(new Date(iso));

/** The retained price window behind one headline, with the chart and exact
 * observations sharing one source and one scale. */
export function FareHistory({ axis, points }: { axis: Axis; points: PricePoint[] }) {
  const [width, height] = [520, 128];
  const plot = { left: 58, right: 12, top: 14, bottom: 14 };
  const values = points.map((point) => point.value);
  const low = Math.min(...values);
  const high = Math.max(...values);
  const x = (index: number) =>
    plot.left + (index * (width - plot.left - plot.right)) / Math.max(1, points.length - 1);
  const y = (value: number) =>
    high === low
      ? height / 2
      : plot.top + (height - plot.top - plot.bottom) * (1 - (value - low) / (high - low));
  const line = points.map((point, index) => `${x(index)},${y(point.value)}`).join(" ");
  const last = points[points.length - 1];

  return (
    <div className="mt-2 grid gap-3 border-t border-neutral-800/80 pt-3 lg:grid-cols-[minmax(0,1fr)_minmax(250px,0.72fr)]">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="h-auto w-full"
        role="img"
        aria-label={`${points.length} retained price observations from ${money(axis, low)} to ${money(axis, high)}`}
      >
        {[...new Set([high, low])].map((value) => (
          <g key={value}>
            <line
              x1={plot.left}
              x2={width - plot.right}
              y1={y(value)}
              y2={y(value)}
              stroke="currentColor"
              strokeDasharray="3 5"
              className="text-neutral-800"
            />
            <text
              x={plot.left - 8}
              y={y(value) + 4}
              textAnchor="end"
              fill="currentColor"
              className="fill-neutral-500 text-[10px]"
            >
              {money(axis, value)}
            </text>
          </g>
        ))}
        <polyline points={line} fill="none" stroke="currentColor" strokeWidth="2.5" className="text-accent" />
        <circle
          cx={x(points.length - 1)}
          cy={y(last.value)}
          r="4"
          className={last.value <= low && points.length > 1 ? "fill-green-400" : "fill-accent"}
        />
      </svg>

      <div className="max-h-44 overflow-y-auto">
        <table className="w-full border-collapse text-left text-xs tabular-nums">
          <thead className="sticky top-0 bg-neutral-900 text-[10px] tracking-[0.08em] text-neutral-500 uppercase">
            <tr>
              <th className="pb-1 font-semibold">First seen</th>
              <th className="pb-1 text-right font-semibold">Price</th>
              <th className="pb-1 text-right font-semibold">Change</th>
            </tr>
          </thead>
          <tbody>
            {[...points].reverse().map((point, reverseIndex) => {
              const index = points.length - 1 - reverseIndex;
              const prior = index > 0 ? points[index - 1].value : null;
              const move = prior === null ? null : whole(point.value) - whole(prior);
              const newFlight = index > 0 && point.flight && point.flight !== points[index - 1].flight ? point.flight : null;
              return (
                <tr key={`${point.at}-${index}`} className="border-t border-neutral-800/70">
                  <td className="py-1.5 text-neutral-400">
                    {moment(point.at)}
                    {newFlight && <span className="ml-1 text-amber-400">now {newFlight}</span>}
                  </td>
                  <td className="py-1.5 text-right font-mono">{money(axis, point.value)}</td>
                  <td className={`py-1.5 text-right font-mono ${move !== null && move < 0 ? "text-green-400" : "text-neutral-500"}`}>
                    {move === null ? "first" : move < 0 ? `${money(axis, -move)} lower` : `${money(axis, move)} higher`}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-neutral-500 lg:col-span-2">
        Repeat checks are folded.
      </p>
    </div>
  );
}
